/**
 * Live-refresh signal for the batch Activity log (STFFLG-3651 follow-up).
 *
 * A module-level pub/sub keyed by batchId. Any code that performs an action the backend
 * records as batch activity — a field edit, an approval, a send-to-payroll, a download —
 * calls `emitBatchActivity(batchId)` in its success path. The Activity panel's data hook and
 * the "View Activity" count hook subscribe, so the badge and an already-open panel refetch
 * and update WITHOUT a full page reload.
 *
 * Deliberately plain module scope, NOT a React context: an emitter can live anywhere (a
 * service method in `payrollApi`, a modal deep in another subtree) and still reach the hooks
 * without a provider wrapping every batch screen.
 *
 * Emit AFTER the action's API promise resolves — the backend writes the activity entry as
 * part of that request, so by resolve time the row exists; emitting earlier would race the
 * write and the refetch could miss it.
 */
type Listener = () => void

const listeners = new Map<string, Set<Listener>>()

/**
 * Subscribe `fn` to activity emitted for `batchId`. Returns an unsubscribe function — call it
 * from an effect cleanup so a closed panel or a changed batchId stops listening and the map
 * does not leak empty sets.
 */
export function subscribeBatchActivity(batchId: string, fn: Listener): () => void {
  let set = listeners.get(batchId)
  if (!set) {
    set = new Set<Listener>()
    listeners.set(batchId, set)
  }
  set.add(fn)

  return () => {
    const current = listeners.get(batchId)
    if (!current) return
    current.delete(fn)
    if (current.size === 0) listeners.delete(batchId)
  }
}

/**
 * Notify every subscriber of `batchId` that a new activity entry was written. A no-op when
 * `batchId` is absent (e.g. a not-yet-saved batch) or has no listeners.
 *
 * Iterates a snapshot of the listener set so a subscriber that unsubscribes itself inside its
 * own callback cannot mutate the set mid-iteration.
 */
export function emitBatchActivity(batchId: string | undefined | null): void {
  if (!batchId) return
  const set = listeners.get(batchId)
  if (!set || set.size === 0) return
  for (const fn of Array.from(set)) fn()
}
